import axios from 'axios';
import type { HttpBackendOptions } from 'i18next-http-backend';

import type { ITranslationResponse, ITranslationsPayload } from '@shared/types/i18n';

import { getPublicRequestOptions } from '../api/configs/requestOption';
import { GET_TRANSLATIONS_URL } from '../api/configs/URL';
import type { CustomAxiosRequestConfig, GenericApiResponse } from '../api/types';

import { getI18nCacheKey, getI18nLastModifiedCacheKey, getI18nValidCacheLastModified } from '.';

export const getI18nHttpBackendOptions = (): HttpBackendOptions => {
  const backendOptions: HttpBackendOptions = {
    loadPath: GET_TRANSLATIONS_URL,
    parseLoadPayload: (languages, namespaces) => ({
      Language: languages[0],
      Namespace: namespaces[0],
      LastModified: getI18nValidCacheLastModified(languages[0], namespaces[0]),
      Source: 'Web',
    }),
    request: (_options, url, payload: ITranslationsPayload, callback) => {
      const { Language, Namespace } = payload;
      axios
        .post(url, payload, {
          // Translations load at module init, before <OidcProvider> mounts, so no
          // access token is available to attach.
          ...getPublicRequestOptions(true, true),
          skipRequestInterceptor: true,
        } as CustomAxiosRequestConfig)
        .then((res) => {
          const response = res.data as GenericApiResponse<ITranslationResponse>;
          const result = response.Result;

          const lastModifiedCacheKey = getI18nLastModifiedCacheKey(Language, Namespace);

          if (!result.Translations) {
            const localStorageCache = localStorage.getItem(getI18nCacheKey(Language, Namespace));
            if (!localStorageCache) {
              localStorage.removeItem(lastModifiedCacheKey);
              throw new Error('No translations returned and no cache available');
            }
            callback(null, { status: 200, data: localStorageCache });
            return;
          }

          // store last modified date & update cache
          if (result?.LastModified) {
            localStorage.setItem(lastModifiedCacheKey, result.LastModified);
          }
          callback(null, {
            status: 200,
            data: JSON.stringify(result.Translations),
          });
        })
        .catch((err) => {
          console.warn('Failed to load translations:', err);
          callback(err, null);
        });
    },
  };

  return backendOptions;
};
